import { motion, useReducedMotion } from 'framer-motion'
import { useState } from 'react'

import { ButtonRadio } from '@/components/base/ButtonRadio'
import { Trans } from '@/components/Trans'
import { BILLING_METHODS, type BillingMethod, DURATION, type Duration } from '@/consts/order'
import { cn } from '@/utils/style'

const FIGMA_SPRING_EASE = (t: number) =>
  1 - Math.exp(-t * 7.4426) * (Math.cos(t * 10.5254) + 0.7071 * Math.sin(t * 10.5254))

const PROMOTION_HOVER_TRANSITION = {
  duration: 1.001,
  ease: FIGMA_SPRING_EASE,
}

const SUBSCRIPTION_PRICE_MULTIPLIER: Record<Duration, number> = {
  [DURATION.DAILY]: 0.98,
  [DURATION.MONTHLY]: 0.95,
}

function DurationDiscountBadge({ duration, selected }: { duration: Duration; selected: boolean }) {
  const discount = Math.round((1 - SUBSCRIPTION_PRICE_MULTIPLIER[duration]) * 100)

  return (
    <span
      className={cn(
        't-xxs absolute -right-1 -top-1 whitespace-nowrap rounded-[4px] px-1.5 py-0.5 text-white',
        selected
          ? 'bg-[linear-gradient(115deg,#c0d5ff_0%,#9db7ff_25%,#7a99ff_50%,#567aff_75%,#335cff_100%)]'
          : 'bg-neutral-400',
      )}
      data-ai-component="duration-discount"
    >
      <Trans i18nKey="create.durationDiscountBadge" values={{ discount }}>
        {discount}% Off
      </Trans>
    </span>
  )
}

function SubscriptionPromotion({ selected, collapsed }: { selected: boolean; collapsed: boolean }) {
  const shouldReduceMotion = useReducedMotion()
  const emphasized = selected || collapsed
  const transition = shouldReduceMotion ? { duration: 0 } : PROMOTION_HOVER_TRANSITION

  return (
    <>
      {selected ? null : (
        <>
          <motion.span
            aria-hidden
            animate={{ y: collapsed ? 13 : 0 }}
            className="absolute left-0 top-[-13px] z-0 flex h-[22px] w-full items-start justify-center whitespace-nowrap rounded-t-[8px] bg-[linear-gradient(115deg,#c0d5ff_0%,#9db7ff_25%,#7a99ff_50%,#567aff_75%,#335cff_100%)] px-1 pt-0.5 text-[10px] leading-[10px] text-white"
            data-ai-component="subscription-promotion"
            data-node-id="14187:28463"
            initial={false}
            transition={transition}
          >
            <Trans i18nKey="create.subscriptionPromotion">Max to 5% Off</Trans>
          </motion.span>
          <span className="sr-only">
            <Trans i18nKey="create.subscriptionPromotion">Max to 5% Off</Trans>
          </span>
        </>
      )}

      <motion.span
        aria-hidden
        animate={{
          backgroundColor: emphasized
            ? 'rgb(var(--color-table-row-selected))'
            : 'rgb(var(--color-neutral-100))',
        }}
        className="absolute inset-0 z-10 rounded-[8px]"
        data-node-id="14187:28467"
        initial={false}
        transition={transition}
      />

      <motion.span
        animate={{
          color: emphasized ? 'rgb(var(--color-btn-secondary))' : 'rgb(var(--color-text-primary))',
        }}
        className="relative z-20"
        data-node-id="14187:28468"
        initial={false}
        transition={transition}
      >
        <Trans i18nKey="create.billingMethodsOptions.subscription">Subscription</Trans>
      </motion.span>
    </>
  )
}

interface BillingMethodsProps {
  billingMethod: BillingMethod
  duration: Duration
  onBillingMethodChange: (value: BillingMethod) => void
  onDurationChange: (value: Duration) => void
  layout?: 'row' | 'stacked'
}

export function BillingMethods({
  billingMethod,
  duration,
  onBillingMethodChange,
  onDurationChange,
  layout = 'row',
}: BillingMethodsProps) {
  const [subscriptionHovered, setSubscriptionHovered] = useState(false)

  return (
    <div className={layout === 'stacked' ? 'flex flex-col gap-6' : 'flex flex-row gap-4'}>
      <div className="flex flex-col gap-2">
        <div className="text-secondary t-sb">
          <Trans i18nKey="create.billingMethods">Billing Method</Trans>
        </div>
        <ButtonRadio
          className={billingMethod === BILLING_METHODS.SUBSCRIPTION ? undefined : 'pt-[13px]'}
          unselectedClassName="bg-neutral-100"
          value={billingMethod}
          items={[
            {
              label: <Trans i18nKey="create.billingMethodsOptions.payAsYouGo">Pay-as-you-go</Trans>,
              value: BILLING_METHODS.PAY_AS_YOU_GO,
            },
            {
              label: (
                <SubscriptionPromotion
                  collapsed={subscriptionHovered}
                  selected={billingMethod === BILLING_METHODS.SUBSCRIPTION}
                />
              ),
              value: BILLING_METHODS.SUBSCRIPTION,
              buttonProps: {
                className: 'relative isolate w-[120px] !bg-transparent !text-primary',
                onMouseEnter: () => setSubscriptionHovered(true),
                onMouseLeave: () => setSubscriptionHovered(false),
              },
            },
          ]}
          onChange={onBillingMethodChange}
        />
      </div>

      {billingMethod === BILLING_METHODS.SUBSCRIPTION ? (
        <div className="flex flex-col gap-2">
          <div className="text-secondary t-sb">
            <Trans i18nKey="create.duration">Duration</Trans>
          </div>
          <ButtonRadio
            value={duration}
            items={[
              {
                label: (
                  <>
                    <Trans i18nKey="create.durationOptions.daily">Daily</Trans>
                    <DurationDiscountBadge
                      duration={DURATION.DAILY}
                      selected={duration === DURATION.DAILY}
                    />
                  </>
                ),
                value: DURATION.DAILY,
                buttonProps: { className: 'relative w-[120px]' },
              },
              {
                label: (
                  <>
                    <Trans i18nKey="create.durationOptions.monthly">Monthly</Trans>
                    <DurationDiscountBadge
                      duration={DURATION.MONTHLY}
                      selected={duration === DURATION.MONTHLY}
                    />
                  </>
                ),
                value: DURATION.MONTHLY,
                buttonProps: { className: 'relative w-[120px]' },
              },
            ]}
            onChange={onDurationChange}
          />
        </div>
      ) : null}
    </div>
  )
}
